import { useQuery } from '@tanstack/react-query'

import { IPerformanceLog } from '@/types/performanceLog'
import { authenticatedApi } from '@/utils/api'

export const QUERY_KEY_PERFORMANCE_LOGS = 'performance-logs'

interface UsePerformanceLogsParams {
  dateGte?: Date | null
  dateLte?: Date | null
  moverId?: string
  branch?: string
  status?: string
}

async function fetchPerformanceLogs(params: UsePerformanceLogsParams) {
  const searchParams: Record<string, string> = {}

  if (params.dateGte) searchParams.dateGte = params.dateGte.toISOString()
  if (params.dateLte) searchParams.dateLte = params.dateLte.toISOString()
  if (params.moverId) searchParams.moverId = params.moverId
  if (params.branch) searchParams.branch = params.branch
  if (params.status) searchParams.status = params.status

  return authenticatedApi.get('performance-log', { searchParams }).json<IPerformanceLog[]>()
}

export const usePerformanceLogs = (params: UsePerformanceLogsParams) => {
  return useQuery({
    queryKey: [QUERY_KEY_PERFORMANCE_LOGS, params],
    queryFn: () => fetchPerformanceLogs(params),
    enabled: !!(params.dateGte && params.dateLte),
  })
}

/**
 * Fetch all performance logs filtered by status (no date range required).
 * Used on the approvals page.
 */
export const useAllPerformanceLogsByStatus = (status: string) => {
  return useQuery({
    queryKey: [QUERY_KEY_PERFORMANCE_LOGS, 'by-status', status],
    queryFn: () => authenticatedApi.get('performance-log', { searchParams: { status } }).json<IPerformanceLog[]>(),
    enabled: !!status,
  })
}

/**
 * Fetch performance logs for a specific mover (no date range required).
 * Used on the mover's own call-out history view.
 */
export const useMoverPerformanceLogs = (moverId?: string) => {
  return useQuery({
    queryKey: [QUERY_KEY_PERFORMANCE_LOGS, 'mover', moverId],
    queryFn: () =>
      authenticatedApi.get('performance-log', { searchParams: { moverId: moverId! } }).json<IPerformanceLog[]>(),
    enabled: !!moverId,
  })
}
